import { flash, Tabs } from "@xola/ui-kit";
import React, { useCallback, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { Delegate, Guide, Permission } from "types";
import { api } from "../../../../api/api";
import { useAuthorization } from "../../../../hooks/useAuthorization";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { ColumnLayout } from "../../../Layout/ColumnLayout";
import { GuideFormData, InformationTab } from "./InformationTab";
import { PermissionsTab } from "./PermissionsTab";
import { PreferencesTab } from "./PreferencesTab";

interface EditGuideFormProps {
    guide?: Guide;
    delegate?: Delegate;
}

export const EditGuideForm = ({ guide, delegate }: EditGuideFormProps) => {
    const { id, section } = useParams<{ id?: string; section?: string }>();
    const history = useHistory();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    const initialSection = section ?? tabIds[0].id;
    const initialActiveIndex = tabIds.findIndex((tab) => tab.id === section ?? initialSection);
    const [activeTab, setActiveTab] = useState(initialActiveIndex === -1 ? 0 : initialActiveIndex);

    useEffect(() => {
        if (initialActiveIndex >= 0 && activeTab !== initialActiveIndex) {
            setActiveTab(initialActiveIndex);
        }
    }, [initialActiveIndex, activeTab]);

    const { mutateAsync: saveGuide, isLoading } = useMutation(async (payload: GuideFormData) => {
        if (payload.id) {
            return api.put(`/sellers/${payload.seller.id}/guides/${payload.id}`, payload);
        }

        return api.post(`/sellers/${payload.seller.id}/guides`, payload);
    });

    const { mutateAsync: deleteGuidePhoto } = useMutation(
        async ({ sellerId, guideId, mediaId }: { sellerId: ID; guideId: ID; mediaId: ID }) => {
            await api.delete(`/sellers/${sellerId}/guides/${guideId}/medias/${mediaId}`);
        },
    );

    const changeTab = (tabIndex: number) => {
        history.push(`/resources/guides/${id}/edit/${tabIds[tabIndex].id}`);
    };

    const handleSuccess = useCallback(
        ({ data }) => {
            invalidate("guides", guide?.id); // Guide info
            invalidate("delegates"); // List of delegates

            if (!guide?.id) {
                history.push(`/resources/guides/${data.id}/edit`);
                flash.success("Successfully created guide");
            }

            if (guide?.id) {
                flash.success("Successfully updated guide");
            }
        },
        [history, guide, invalidate],
    );

    const handleGuidePhotoDeleted = async (initData: GuideFormData, initGuide?: Guide) => {
        const isGuidePhotoDeleted = initGuide?.picture?.id && !initData.picture;

        if (isGuidePhotoDeleted && initData?.id && initGuide?.picture) {
            await deleteGuidePhoto({
                sellerId: initData.seller.id,
                guideId: initData.id,
                mediaId: initGuide.picture.id,
            });
        }
    };

    const handleSubmit = async (data: GuideFormData) => {
        await handleGuidePhotoDeleted(data, guide);

        await saveGuide(data).then(handleSuccess);
    };

    return (
        <ColumnLayout.Fill>
            <Tabs className="w-full" value={activeTab} onChange={changeTab}>
                <Tabs.Tab>Information</Tabs.Tab>
                <Tabs.Panel>
                    <InformationTab guide={guide} delegate={delegate} isLoading={isLoading} onSubmit={handleSubmit} />
                </Tabs.Panel>

                <Tabs.Tab isHidden={!isGranted(Permission.RESOURCES_GUIDES_ALL_VIEW)} disabled={!guide}>
                    Permission
                </Tabs.Tab>
                <Tabs.Panel>
                    <PermissionsTab />
                </Tabs.Panel>

                <Tabs.Tab isHidden={!isGranted(Permission.RESOURCES_GUIDES_ALL_VIEW)} disabled={!guide}>
                    Preferences
                </Tabs.Tab>
                <Tabs.Panel>
                    <PreferencesTab />
                </Tabs.Panel>
            </Tabs>
        </ColumnLayout.Fill>
    );
};

const tabIds = [{ id: "information" }, { id: "permission" }, { id: "preferences" }];
